import React, { useState, useEffect } from "react";
import { logger } from "@/services/Logger";
import { Button } from "@/components/ui/button";
import { X, Download, Trash2, Bug } from "lucide-react";
import { cn } from "@/lib/utils";

interface DebugPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

export const DebugPanel: React.FC<DebugPanelProps> = ({ isOpen, onClose }) => {
  const [logs, setLogs] = useState(logger.getRecentLogs());
  const [filter, setFilter] = useState<string>("all");
  const [isDebugMode, setIsDebugMode] = useState(
    localStorage.getItem("debug") === "true"
  );

  useEffect(() => {
    if (isOpen) {
      // Refresh logs when panel opens
      setLogs(logger.getRecentLogs());

      // Auto-refresh logs every 2 seconds while open
      const interval = setInterval(() => {
        setLogs(logger.getRecentLogs());
      }, 2000);

      return () => clearInterval(interval);
    }
  }, [isOpen]);

  const handleToggleDebug = () => {
    const newState = !isDebugMode;
    setIsDebugMode(newState);
    logger.setDebugMode(newState);
  };

  const handleClearLogs = () => {
    logger.clearLogs();
    setLogs([]);
  };

  const handleExportLogs = () => {
    const logData = logger.exportLogs();
    const blob = new Blob([logData], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `scanifood-logs-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filteredLogs =
    filter === "all" ? logs : logs.filter((log) => log.level === filter);

  const getLevelColor = (level: string) => {
    switch (level) {
      case "debug":
        return "text-gray-500";
      case "info":
        return "text-blue-500";
      case "warn":
        return "text-yellow-500";
      case "error":
        return "text-red-500";
      default:
        return "text-gray-500";
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className={`fixed inset-y-0 right-0 z-50 w-full md:w-[400px] bg-white shadow-xl transform transition-transform duration-300 ease-in-out ${
        isOpen ? "translate-x-0" : "translate-x-full"
      }`}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b bg-white">
        <div className="flex items-center gap-2">
          <Bug className="w-5 h-5" />
          <h2 className="font-semibold">Debug Console</h2>
          <span className="text-xs text-gray-500">
            ({filteredLogs.length} logs)
          </span>
        </div>
        <Button size="sm" variant="ghost" onClick={onClose}>
          <X className="w-4 h-4" />
        </Button>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap gap-2 p-4 border-b bg-white">
        <Button
          size="sm"
          variant={isDebugMode ? "default" : "outline"}
          onClick={handleToggleDebug}
        >
          Debug: {isDebugMode ? "ON" : "OFF"}
        </Button>

        <div className="flex gap-1">
          <Button
            size="sm"
            variant={filter === "all" ? "default" : "outline"}
            onClick={() => setFilter("all")}
          >
            All
          </Button>
          <Button
            size="sm"
            variant={filter === "error" ? "default" : "outline"}
            onClick={() => setFilter("error")}
          >
            Errors
          </Button>
          <Button
            size="sm"
            variant={filter === "warn" ? "default" : "outline"}
            onClick={() => setFilter("warn")}
          >
            Warnings
          </Button>
          <Button
            size="sm"
            variant={filter === "info" ? "default" : "outline"}
            onClick={() => setFilter("info")}
          >
            Info
          </Button>
        </div>

        <div className="flex gap-1 ml-auto">
          <Button size="sm" variant="outline" onClick={handleClearLogs}>
            <Trash2 className="w-4 h-4" />
          </Button>
          <Button size="sm" variant="outline" onClick={handleExportLogs}>
            <Download className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Logs */}
      <div className="overflow-y-auto max-h-[calc(100vh-200px)] bg-white">
        {filteredLogs.length === 0 ? (
          <div className="text-center text-gray-500 py-8 p-4">
            No logs to display
          </div>
        ) : (
          <div className="p-4">
            {filteredLogs.slice().reverse().map((log, index) => (
              <div
                key={index}
                className={cn(
                  "p-2 rounded border mb-2 font-mono text-xs",
                  log.level === "error"
                    ? "bg-red-50 border-red-200"
                    : log.level === "warn"
                    ? "bg-yellow-50 border-yellow-200"
                    : "bg-gray-50 border-gray-200"
                )}
              >
                <div className="flex items-start gap-2">
                  <span
                    className={cn(
                      "font-semibold uppercase",
                      getLevelColor(log.level)
                    )}
                  >
                    {log.level}
                  </span>
                  <span className="text-gray-600">
                    {new Date(log.timestamp).toLocaleTimeString()}
                  </span>
                  {log.context && (
                    <span className="text-purple-600">[{log.context}]</span>
                  )}
                </div>
                <div className="mt-1">{log.message}</div>
                {log.data && (
                  <pre className="mt-1 text-xs text-gray-600 overflow-x-auto">
                    {JSON.stringify(log.data, null, 2)}
                  </pre>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="p-4 border-t text-xs text-gray-500 bg-white">
        <p>Tip: Triple-tap the logo to open this panel on mobile</p>
        <p className="mt-1">
          Buffer: {logger.getBufferInfo().count}/{logger.getBufferInfo().maxCount} logs 
          (~{logger.getBufferInfo().approximateSizeKB}KB)
        </p>
      </div>
    </div>
  );
};
